
import { useState } from 'react';

export default function LeadForm() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);

      const response = await fetch('https://readdy.ai/api/form/d55cqrqs3mmh6cm90i9g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '' });
        setTimeout(() => {
          setIsOpen(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="group bg-emerald-600 text-white px-6 sm:px-10 py-3 sm:py-5 rounded-full text-base sm:text-lg font-bold hover:bg-emerald-700 hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap cursor-pointer transform hover:scale-105 w-full sm:w-auto"
      >
        <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
          <i className="ri-user-add-line text-xl sm:text-2xl"></i>
        </div>
        <span className="text-sm sm:text-base">Get Cheaper Leads Now</span>
        <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
          <i className="ri-arrow-right-line text-xl sm:text-2xl group-hover:translate-x-1 transition-transform"></i>
        </div>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative animate-slideUp max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 sm:top-6 right-4 sm:right-6 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="mb-6">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-emerald-100 rounded-xl sm:rounded-2xl mb-4">
                <i className="ri-plant-line text-2xl sm:text-3xl text-emerald-600"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Start Generating Quality Leads</h2>
              <p className="text-sm sm:text-base text-gray-600">Get high-quality landscaping leads for under $5 each. Fill out the form below to get started.</p>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form id="lead-capture-form" className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors text-base"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors text-base"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors text-base"
                  placeholder="john@landscaping.com"
                />
              </div>

              {submitStatus === 'success' && (
                <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-xl text-emerald-600"></i>
                  </div>
                  <p className="text-sm sm:text-base text-emerald-800 font-semibold">Thank you! We'll contact you soon.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-error-warning-fill text-xl text-red-600"></i>
                  </div>
                  <p className="text-sm sm:text-base text-red-800 font-semibold">Something went wrong. Please try again.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-emerald-600 text-white px-6 py-4 rounded-xl text-base font-bold hover:bg-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Get More Leads'}
              </button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              We respect your privacy. Your information will never be shared.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
